import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api, shareContent } from "@/lib/apiClient";
import { Reveal, MaskLineInView } from "./Reveal";
import ChatPanel from "./ChatPanel";
import { toast } from "sonner";
import { MessageCircle, Share2 } from "lucide-react";

export default function Ministers() {
  const [ministers, setMinisters] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/ministers").then((r) => setMinisters(r.data)).catch(() => {});
  }, []);

  const openChat = (m) => { setActive(m); setOpen(true); };

  const share = async (e, m) => {
    e.stopPropagation();
    const result = await shareContent({
      title: `${m.name} — F.O.L.W.A.C.`,
      text: `Speak with ${m.name} (${m.role}) — an autonomous AI apostolic minister at Fountain of Living Water Apostolic Church.`,
      url: `${window.location.origin}/#ministers`,
    });
    if (result === "copied") toast.success("Link copied — share it anywhere.");
  };

  return (
    <section id="ministers" className="relative py-28 md:py-40 bg-[#05030A]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><span className="section-label">01 — The Autonomous Ministry</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>Nine ministers.</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">Always awake. Always in prayer.</span></MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-lg text-[#B4ADC1] font-light mt-6 leading-relaxed">
              Nine authentic men & women of God — each carrying one fruit of the Spirit (Galatians 5:22–23) — ready to listen, pray, teach apostolic truth, and walk with you toward the Living Water. Choose the one your heart needs.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {ministers.map((m, i) => (
            <motion.button
              key={m.id}
              data-testid={`minister-card-${m.id}`}
              onClick={() => openChat(m)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative text-left glass rounded-2xl p-6 overflow-hidden hover:-translate-y-2 transition-transform duration-500"
            >
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-20 group-hover:opacity-45 transition-opacity duration-500"
                style={{ background: m.color }}
              />
              <div className="relative flex items-start justify-between">
                {m.photo ? (
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden border transition-transform duration-500 group-hover:scale-110"
                    style={{ borderColor: m.color }}
                  >
                    <img src={m.photo} alt={m.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center font-display text-2xl border transition-transform duration-500 group-hover:scale-110"
                    style={{ borderColor: m.color, color: m.color, background: `${m.color}12` }}
                  >
                    {m.initials}
                  </div>
                )}
                <span className="flex items-center gap-2">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => share(e, m)}
                    className="p-1.5 rounded-full text-[#7A708A] hover:text-white hover:bg-white/10 transition-colors"
                    data-testid={`minister-share-${m.id}`}
                    aria-label={`Share ${m.name}`}
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </span>
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full border" style={{ borderColor: `${m.color}55`, color: m.color }}>
                    {m.fruit}
                  </span>
                </span>
              </div>

              <h3 className="relative font-display text-2xl text-white mt-6 leading-tight">{m.name}</h3>
              <p className="relative text-xs font-mono uppercase tracking-widest text-[#7A708A] mt-1">{m.role}</p>
              <p className="relative text-sm text-[#B4ADC1] font-light mt-4 leading-relaxed">{m.focus}</p>

              <div className="relative flex items-center gap-2 mt-6 text-sm" style={{ color: m.color }}>
                <MessageCircle className="w-4 h-4" />
                <span className="font-medium">Begin a conversation</span>
                <span className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <ChatPanel minister={active} open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
